import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getMe as getMeRequest,
  login as loginRequest,
  logout as logoutRequest,
  renewPassword as renewPasswordRequest,
  setActiveBranch as setActiveBranchRequest,
  setActiveCounter as setActiveCounterRequest,
} from '../api/authApi';

const AUTH_STORAGE_KEY = 'playhouse.auth';
/** Bump when persisted auth shape changes; older blobs drop cached session context. */
export const AUTH_STORAGE_SCHEMA_VERSION = 3;

const EMPTY_SESSION_CONTEXT = {
  activeBranch: null,
  accessibleBranchCount: 0,
  needsBranchSelection: false,
  activeCounter: null,
  activeBranchCounterCount: 0,
  needsCounterSelection: false,
  branchHasCounters: false,
};

const EMPTY_AUTH_STATE = {
  token: null,
  user: null,
  sessionTimeoutMinutes: null,
  entrySettings: null,
  pricingSettings: null,
  uiTheme: null,
  organization: null,
  branchesInitialized: null,
  ...EMPTY_SESSION_CONTEXT,
};

const normalizeActiveBranch = (raw) => {
  if (!raw?.id || !raw?.name) {
    return null;
  }
  const status = raw.status;
  if (status !== 'active' && status !== 'inactive') {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name,
    status,
  };
};

const normalizeActiveCounter = (raw, activeBranch) => {
  if (!raw?.id || !raw?.name) {
    return null;
  }
  const status = raw.status;
  if (status !== 'active' && status !== 'inactive') {
    return null;
  }
  const branchId = raw.branch_id ?? raw.branchId ?? null;
  if (activeBranch?.id != null && branchId != null && branchId !== activeBranch.id) {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name,
    status,
    branchId,
  };
};

export const applySessionContextPayload = (payload) => {
  const activeBranch = normalizeActiveBranch(payload?.active_branch);
  const activeCounter = normalizeActiveCounter(payload?.active_counter, activeBranch);
  let needsCounterSelection = Boolean(payload?.needs_counter_selection);
  const branchHasCounters = Boolean(payload?.branch_has_counters);

  if (needsCounterSelection && activeCounter?.status === 'active') {
    needsCounterSelection = false;
  }
  if (!branchHasCounters) {
    needsCounterSelection = false;
  }
  if (branchHasCounters && !activeCounter && !needsCounterSelection) {
    needsCounterSelection = true;
  }

  return {
    activeBranch,
    accessibleBranchCount: payload?.accessible_branch_count ?? 0,
    needsBranchSelection: Boolean(payload?.needs_branch_selection),
    activeCounter,
    activeBranchCounterCount: payload?.active_branch_counter_count ?? 0,
    needsCounterSelection,
    branchHasCounters,
  };
};

/** @deprecated Use applySessionContextPayload — branch-only slice for backward compatibility */
export const applySessionBranchPayload = (payload) => {
  const context = applySessionContextPayload(payload);
  return {
    activeBranch: context.activeBranch,
    accessibleBranchCount: context.accessibleBranchCount,
    needsBranchSelection: context.needsBranchSelection,
  };
};

export const pickPersistedAuth = (authState) => ({
  schemaVersion: AUTH_STORAGE_SCHEMA_VERSION,
  token: authState.token,
  user: authState.user,
  sessionTimeoutMinutes: authState.sessionTimeoutMinutes ?? null,
  entrySettings: authState.entrySettings ?? null,
  pricingSettings: authState.pricingSettings ?? null,
  uiTheme: authState.uiTheme ?? null,
  organization: authState.organization ?? null,
  branchesInitialized:
    authState.branchesInitialized === undefined ? null : Boolean(authState.branchesInitialized),
});

export const loadAuthState = () => {
  if (typeof window === 'undefined') {
    return { ...EMPTY_AUTH_STATE };
  }

  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) {
    return { ...EMPTY_AUTH_STATE };
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.token) {
      return { ...EMPTY_AUTH_STATE };
    }

    return {
      ...EMPTY_SESSION_CONTEXT,
      token: parsed.token,
      user: parsed.user || null,
      sessionTimeoutMinutes: parsed.sessionTimeoutMinutes ?? null,
      entrySettings: parsed.entrySettings ?? null,
      pricingSettings: parsed.pricingSettings ?? null,
      uiTheme: parsed.uiTheme ?? null,
      organization: parsed.organization ?? null,
      branchesInitialized:
        parsed.branchesInitialized === undefined ? null : Boolean(parsed.branchesInitialized),
    };
  } catch (error) {
    return { ...EMPTY_AUTH_STATE };
  }
};

const persistAuthState = (authState) => {
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(pickPersistedAuth(authState)));
};

const clearAuthState = () => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => loadAuthState());
  const [isChecking, setIsChecking] = useState(Boolean(authState.token));

  const isAuthenticated = useMemo(
    () => Boolean(authState.token && authState.user),
    [authState],
  );

  const requiresPasswordRenewal = useMemo(
    () => Boolean(authState.user?.require_password_renewal),
    [authState.user],
  );

  const login = useCallback(async ({ username, password }) => {
    setIsChecking(true);
    try {
      const response = await loginRequest({ username, password });
      const nextAuthState = {
        token: response.token,
        user: response.user,
        sessionTimeoutMinutes: response.session_timeout_minutes ?? null,
        entrySettings: response.entry_settings ?? null,
        pricingSettings: response.pricing_settings ?? null,
        uiTheme: response.ui_theme ?? null,
        organization: response.organization ?? null,
        branchesInitialized: Boolean(response.branches_initialized),
        ...applySessionContextPayload(response),
      };
      setAuthState(nextAuthState);
      persistAuthState(nextAuthState);
      return nextAuthState;
    } finally {
      setIsChecking(false);
    }
  }, []);

  const renewPassword = useCallback(
    async (newPassword, confirmPassword) => {
      const token = authState.token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await renewPasswordRequest(token, {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      setAuthState((prev) => {
        if (!prev.token) {
          return prev;
        }
        const nextAuthState = {
          token: prev.token,
          user: response.user,
          sessionTimeoutMinutes:
            response.session_timeout_minutes ?? prev.sessionTimeoutMinutes ?? null,
          entrySettings: response.entry_settings ?? null,
          pricingSettings: response.pricing_settings ?? null,
          uiTheme: response.ui_theme ?? prev.uiTheme ?? null,
          organization: response.organization ?? prev.organization ?? null,
          branchesInitialized:
            response.branches_initialized === undefined
              ? prev.branchesInitialized
              : Boolean(response.branches_initialized),
          ...applySessionContextPayload(response),
        };
        persistAuthState(nextAuthState);
        return nextAuthState;
      });
      return response;
    },
    [authState.token],
  );

  const logout = useCallback(async () => {
    const token = authState.token;
    setAuthState({ ...EMPTY_AUTH_STATE });
    clearAuthState();

    if (token) {
      await logoutRequest(token);
    }
  }, [authState.token]);

  const refreshSession = useCallback(async () => {
    const token = authState.token;
    if (!token) {
      return null;
    }

    try {
      const response = await getMeRequest(token);
      const user = response?.user || response;
      setAuthState((prev) => {
        if (!prev.token) {
          return prev;
        }
        const nextAuthState = {
          token: prev.token,
          user,
          sessionTimeoutMinutes:
            response?.session_timeout_minutes ?? prev.sessionTimeoutMinutes ?? null,
          entrySettings: response?.entry_settings ?? null,
          pricingSettings: response?.pricing_settings ?? null,
          uiTheme: response?.ui_theme ?? prev.uiTheme ?? null,
          organization: response?.organization ?? prev.organization ?? null,
          branchesInitialized:
            response?.branches_initialized === undefined
              ? prev.branchesInitialized
              : Boolean(response.branches_initialized),
          ...applySessionContextPayload(response),
        };
        persistAuthState(nextAuthState);
        return nextAuthState;
      });
      return response;
    } catch (error) {
      setAuthState({ ...EMPTY_AUTH_STATE });
      clearAuthState();
      return null;
    }
  }, [authState.token]);

  useEffect(() => {
    if (!authState.token) {
      setIsChecking(false);
      return;
    }

    let isActive = true;
    setIsChecking(true);
    refreshSession().finally(() => {
      if (isActive) {
        setIsChecking(false);
      }
    });

    return () => {
      isActive = false;
    };
    // Restore persisted sessions once on mount; login() owns fresh session state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBranchesInitialized = useCallback((value) => {
    setAuthState((prev) => {
      if (!prev.token) {
        return prev;
      }
      const nextAuthState = {
        ...prev,
        branchesInitialized: Boolean(value),
      };
      persistAuthState(nextAuthState);
      return nextAuthState;
    });
  }, []);

  const selectActiveBranch = useCallback(
    async (branchId) => {
      const token = authState.token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await setActiveBranchRequest(token, branchId);
      setAuthState((prev) => {
        if (!prev.token) {
          return prev;
        }
        const nextAuthState = {
          ...prev,
          ...applySessionContextPayload(response),
        };
        persistAuthState(nextAuthState);
        return nextAuthState;
      });
      return response;
    },
    [authState.token],
  );

  const selectActiveCounter = useCallback(
    async (counterId) => {
      const token = authState.token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await setActiveCounterRequest(token, counterId);
      setAuthState((prev) => {
        if (!prev.token) {
          return prev;
        }
        const nextAuthState = {
          ...prev,
          ...applySessionContextPayload(response),
        };
        persistAuthState(nextAuthState);
        return nextAuthState;
      });
      return response;
    },
    [authState.token],
  );

  const value = useMemo(
    () => ({
      authState,
      isAuthenticated,
      isChecking,
      requiresPasswordRenewal,
      login,
      renewPassword,
      logout,
      refreshSession,
      setBranchesInitialized,
      selectActiveBranch,
      selectActiveCounter,
      activeCounter: authState.activeCounter,
      activeBranchCounterCount: authState.activeBranchCounterCount,
      needsCounterSelection: authState.needsCounterSelection,
      branchHasCounters: authState.branchHasCounters,
    }),
    [
      authState,
      isAuthenticated,
      isChecking,
      requiresPasswordRenewal,
      login,
      renewPassword,
      logout,
      refreshSession,
      setBranchesInitialized,
      selectActiveBranch,
      selectActiveCounter,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Branch id for server-scoped list hooks; optional override for tests. */
export const useActiveBranchId = (override) => {
  const context = useContext(AuthContext);
  if (override !== undefined) {
    return override;
  }
  return context?.authState?.activeBranch?.id ?? null;
};
